import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, CardEyebrow, CardTitle, CardContent } from '@bookrcs/ui';
import { api } from '../lib/api.js';
import { ApiError } from '../lib/query.js';
import { useAuth } from '../lib/auth.js';

const phoneSchema = z.object({
  phone: z.string().regex(/^\+[1-9]\d{1,14}$/, 'Format E.164 attendu, ex: +33612345678'),
});

const otpSchema = z.object({
  code: z.string().regex(/^\d{6}$/, '6 chiffres attendus'),
});

type Step = 'phone' | 'otp';

export default function LoginRoute() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState<string>('');
  const [topError, setTopError] = useState<string | null>(null);
  const { refresh } = useAuth();

  const phoneForm = useForm<z.infer<typeof phoneSchema>>({ resolver: zodResolver(phoneSchema) });
  const otpForm = useForm<z.infer<typeof otpSchema>>({ resolver: zodResolver(otpSchema) });

  return (
    <main className="min-h-screen grid place-items-center p-6 bg-[var(--background)]">
      <Card variant="focus" className="w-full max-w-md">
        <CardEyebrow>BOOKRCS pro</CardEyebrow>
        <CardTitle>Connexion</CardTitle>
        <CardContent>
          {topError ? <p className="text-[var(--urgent)] text-sm mb-3">{topError}</p> : null}

          {step === 'phone' ? (
            <form
              className="grid gap-3"
              onSubmit={phoneForm.handleSubmit(async (values) => {
                setTopError(null);
                try {
                  await api('/auth/pro/otp', { method: 'POST', json: { phone: values.phone } });
                  setPhone(values.phone);
                  setStep('otp');
                } catch (err) {
                  setTopError(err instanceof ApiError ? err.message : 'Erreur réseau');
                }
              })}
            >
              <label className="grid gap-1 text-sm font-bold text-[var(--text-muted)]">
                Téléphone (E.164)
                <input
                  {...phoneForm.register('phone')}
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+33612345678"
                  className="bg-[var(--elevated)] border border-[var(--border)] rounded-[var(--radius-control)] px-3 py-3 text-[var(--text)] font-mono focus:outline-2 focus:outline-[var(--accent)]"
                />
                {phoneForm.formState.errors.phone ? (
                  <span className="text-[var(--urgent)] text-xs font-normal">
                    {phoneForm.formState.errors.phone.message}
                  </span>
                ) : null}
              </label>
              <Button type="submit" disabled={phoneForm.formState.isSubmitting}>
                Recevoir le code
              </Button>
            </form>
          ) : (
            <form
              className="grid gap-3"
              onSubmit={otpForm.handleSubmit(async (values) => {
                setTopError(null);
                try {
                  await api('/auth/pro/verify', {
                    method: 'POST',
                    json: { phone, code: values.code },
                  });
                  await refresh();
                } catch (err) {
                  setTopError(err instanceof ApiError ? err.message : 'Erreur réseau');
                }
              })}
            >
              <label className="grid gap-1 text-sm font-bold text-[var(--text-muted)]">
                Code reçu par SMS
                <input
                  {...otpForm.register('code')}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  placeholder="000000"
                  className="bg-[var(--elevated)] border border-[var(--border)] rounded-[var(--radius-control)] px-3 py-3 text-[var(--text)] font-mono text-2xl tracking-widest text-center focus:outline-2 focus:outline-[var(--accent)]"
                />
                {otpForm.formState.errors.code ? (
                  <span className="text-[var(--urgent)] text-xs font-normal">
                    {otpForm.formState.errors.code.message}
                  </span>
                ) : null}
              </label>
              <Button type="submit" disabled={otpForm.formState.isSubmitting}>
                Valider
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setTopError(null);
                  otpForm.reset();
                }}
                className="text-xs text-[var(--text-faint)] hover:text-[var(--text)] transition-colors"
              >
                Changer de numéro
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
